import * as React from 'react';
import { injectInitialProps } from '../../../utilities/components/Props';
import {
    ItemDeleteAction as BaseItemDeleteAction,
    DeleteAction as BaseDeleteAction,
    IItemDeleteActionProps as IBaseItemDeleteActionProps,
    IDeleteActionProps as IBaseDeleteActionProps
} from '../../base/actions/delete/DeleteAction';
import { CancelError } from '../../../utilities/async/CancelError';
import { ConfirmationDialog } from '../../confirmationDialog/ConfirmationDialog';
import { IDeleteWizardProps } from '../../../controls/deleteActionControl';
import ActionStrings from '../../scope/actions/strings.resx';
import DeleteStrings from '../../../operations/deleteItems/DeleteStrings.resx';
import { WithItemCache, IItemCacheStateProps } from '../../withItemCache/WithItemCache';
import { isCheckedOutFacet } from '../../../facets/ItemFacets';
import { WarningDialog } from '../../warningDialog/WarningDialog';
import ItemStrings from '../../../models/items/ItemStrings.resx';
import { IDecoratedProps } from '../../../utilities/components/Decorators';
import { IOperationActionCreator } from '../../../store/actions/implementOperation';
import { deleteItemsOperation, deleteProgressKeyFacet } from '../../../operations/deleteItems';
import { implementOperationWithProgress } from '../../../operations/progress';
import { deleteProgressAddon } from '../../../operations/deleteItems/DeleteProgressAddon';
import { enableDeleteCancelConfirmationStringsConfigurationFacet } from '../../../facets/ConfigurationFacets';
import { getConfiguration } from '../../../models/configuration/getConfiguration';

class DeleteWizard extends React.PureComponent<IDeleteWizardProps> {
    public render(): JSX.Element {
        const {
            resolve,
            reject,
            onDelete,
            defaultToDelete = false,
            itemKeys
        } = this.props;

        const onConfirm = async () => {
            resolve();

            await onDelete();
        };

        const onCancel = async () => {
            reject(new CancelError());
        };

        return (
            <WithItemCache>
                { ({ itemCache }: IItemCacheStateProps) => {
                    const {
                        enableDeleteCancelConfirmationStrings
                    } = getConfiguration(itemCache, {
                        enableDeleteCancelConfirmationStrings: enableDeleteCancelConfirmationStringsConfigurationFacet
                    });

                    const showCheckedOutWarning = itemKeys.length === 1 && itemKeys.some((itemKey: string) => {
                        const isCheckedOut = itemCache.demandItemFacet(isCheckedOutFacet, itemKey);

                        const isCheckedOutByOther = isCheckedOut && isCheckedOut.byOther;

                        return !!isCheckedOutByOther;
                    });

                    if (showCheckedOutWarning) {
                        return (
                            <WarningDialog
                                onCancel={ onCancel }
                                cancelText={ ActionStrings.cancelActionText }
                                title={ ItemStrings.checkedOutOrLockedWarningTitle }
                                prompt={ ItemStrings.checkedOutOrLockedWarning }
                            />
                        );
                    }

                    return (
                        <ConfirmationDialog
                            onCancel={ onCancel }
                            onConfirm={ onConfirm }
                            yesText={ enableDeleteCancelConfirmationStrings ? DeleteStrings.deleteConfirmButtonText : undefined }
                            noText={ enableDeleteCancelConfirmationStrings ? DeleteStrings.deleteCancelButtonText : undefined }
                            defaultToYes={ defaultToDelete }
                            title={ ActionStrings.deleteConfirmTitle }
                            prompt={ ActionStrings.deleteConfirmText }
                        />
                    );
                } }
            </WithItemCache>
        );
    }
}

const deleteItemsWithProgress: IOperationActionCreator<typeof deleteItemsOperation.type> = implementOperationWithProgress({
    operationTypeFacet: deleteItemsOperation,
    progressKeyFacet: deleteProgressKeyFacet,
    initializeBatch: () => deleteProgressAddon
});

function injectDeleteWizard(): Pick<IBaseItemDeleteActionProps, 'deleteWizard' | 'deleteItems'> {
    return {
        deleteWizard: DeleteWizard,
        deleteItems: deleteItemsWithProgress
    };
}

export type IItemDeleteActionProps = IDecoratedProps<IBaseItemDeleteActionProps, Pick<IBaseItemDeleteActionProps, 'deleteWizard'>, {}>;

export const ItemDeleteAction: React.ComponentType<IItemDeleteActionProps> =
    injectInitialProps(injectDeleteWizard)(BaseItemDeleteAction);

export type IDeleteActionProps = IDecoratedProps<IBaseDeleteActionProps, Pick<IBaseDeleteActionProps, 'deleteWizard'>, {}>;

export const DeleteAction: React.ComponentType<IDeleteActionProps> =
    injectInitialProps(injectDeleteWizard)(BaseDeleteAction);
